import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Mail, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SERVICES } from "@/data/content";
import ProtectedLogo from "@/components/bits/ProtectedLogo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/process", label: "Process" },
  { to: "/faq", label: "FAQ" },
];

const AUDIENCES = [
  { to: "/businesses", label: "Businesses", style: "bg-navy text-white border border-navy hover:bg-darknavy", mobileStyle: "bg-navy text-white border border-navy" },
  { to: "/careers", label: "Students", style: "border border-navy/30 text-navy hover:border-gold hover:text-gold", mobileStyle: "border border-white/30 text-white" },
  { to: "/colleges", label: "Colleges & TPOs", style: "border border-gold/60 text-gold hover:bg-gold/10", mobileStyle: "border border-gold/60 text-lightgold" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAudienceActive = (to) => pathname === to || pathname.startsWith(to + "/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 bg-white/95 backdrop-blur-xl border-b ${
        scrolled ? "border-[#E2E7EF] shadow-[0_4px_24px_rgba(7,29,69,0.08)] py-2" : "border-[#E2E7EF]/60 py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo" className="shrink-0 group">
          <div className="rounded-lg px-1.5 py-1">
            <ProtectedLogo alt="Maqline Solutions" className="h-8 sm:h-9 w-auto transition-transform duration-300 group-hover:scale-[1.03]" />
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {LINKS.map((l) =>
            l.label === "Services" ? (
              <div key={l.to} className="relative group" data-testid="nav-services-dropdown">
                <NavLink
                  to={l.to}
                  data-testid="nav-services"
                  className={({ isActive }) =>
                    `gold-underline text-[13px] uppercase tracking-[0.14em] font-semibold transition-colors duration-300 inline-flex items-center gap-1 ${
                      isActive ? "text-gold" : "text-ink/70 hover:text-navy"
                    }`
                  }
                >
                  Services <ChevronDown size={13} className="group-hover:rotate-180 transition-transform duration-300" />
                </NavLink>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="bg-darknavy border border-gold/20 rounded-sm shadow-[0_24px_60px_rgba(6,27,58,0.5)] w-72 py-2">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        className="flex items-center gap-3 px-5 py-2.5 text-sm text-white/70 hover:text-lightgold hover:bg-white/5 transition-colors duration-200"
                      >
                        <span className="font-mono text-[10px] text-gold font-bold">{s.num}</span> {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `gold-underline text-[13px] uppercase tracking-[0.14em] font-semibold transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-ink/70 hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-2" data-testid="audience-selector">
          {AUDIENCES.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              data-testid={`nav-audience-${a.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`text-[11px] uppercase tracking-[0.12em] font-bold px-4 py-2.5 rounded-sm transition-colors duration-300 ${
                isAudienceActive(a.to) ? "bg-gold text-darknavy border border-gold" : a.style
              }`}
            >
              {a.label}
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="nav-contact"
            className="ml-1 text-navy/70 hover:text-gold transition-colors duration-300 p-2"
            aria-label="Contact"
          >
            <Mail size={17} />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 transition-colors duration-300 ${open ? "text-gold" : "text-navy"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            data-lenis-prevent
            className="lg:hidden fixed inset-0 bg-white z-40 pt-24 px-6 pb-10 overflow-y-auto"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-[11px] uppercase tracking-[0.25em] text-gold font-bold mb-3">I am here for</p>
            <div className="grid grid-cols-1 gap-2 mb-8">
              {AUDIENCES.map((a) => (
                <button
                  key={a.to}
                  data-testid={`mobile-audience-${a.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => { setOpen(false); navigate(a.to); }}
                  className={`text-left text-sm uppercase tracking-[0.12em] font-bold px-5 py-4 rounded-sm ${
                    isAudienceActive(a.to) ? "bg-gold text-darknavy border border-gold" : a.style
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
            <div className="h-px bg-ink/10 mb-6" />
            <div className="flex flex-col gap-1">
              {[...LINKS, { to: "/careers", label: "Careers & Jobs" }, { to: "/internships", label: "Internships" }, { to: "/contact", label: "Contact" }].map((l) => (
                <button
                  key={l.to + l.label}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => { setOpen(false); navigate(l.to); }}
                  className="text-left text-navy/85 hover:text-gold font-display text-2xl py-2.5 transition-colors duration-200 flex items-center justify-between"
                >
                  {l.label}
                  <ChevronDown size={18} className="-rotate-90 text-gold/60" />
                </button>
              ))}
            </div>
            <div className="mt-8 text-muted text-sm">
              <p>info@maqlinesolutions.com</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
